import {
  Building2,
  ClipboardList,
  CreditCard,
  Database,
  Eye,
  FileText,
  Landmark,
  type LucideIcon,
  Mail,
  MapPin,
  PenLine,
  ScanFace,
  ShieldCheck,
  Smartphone,
  TrendingUp,
  Upload,
  Zap,
} from 'lucide-react';

import { MaskIcon } from '@/components/ui/MaskIcon';
import { cn } from '@/lib/utils';

import { STEP_TYPE_META } from './types';

// ── Designer step icons ───────────────────────────────────────────────────────
// The designer drew one icon per verification API (react-new-html
// `assets/images/*.svg`, mapped in LatestWorkflowTable.tsx `STEP_ICON_MAP`).
// They're stroke-only SVGs hard-coded to #445968, so they can't be recoloured
// with `currentColor` — the design paints them with `mask-image` instead, which
// is what `.sc-step-icon` in globals.css does. Copied verbatim into
// assets/icons/steps/ (kebab-cased to match this repo's asset convention).

import aadhaarIcon from '@/assets/icons/steps/aadhaar-verification.svg';
import businessIdIcon from '@/assets/icons/steps/business-id.svg';
import corporateIcon from '@/assets/icons/steps/corporate-verification.svg';
import faceMatchIcon from '@/assets/icons/steps/face-match.svg';
import gstIcon from '@/assets/icons/steps/gst-verification.svg';
import idVerificationIcon from '@/assets/icons/steps/id-verification.svg';
import livenessIcon from '@/assets/icons/steps/liveness.svg';
import msmeIcon from '@/assets/icons/steps/msme-verification.svg';
import ocrIcon from '@/assets/icons/steps/ocr.svg';
import panIcon from '@/assets/icons/steps/pan-verification.svg';
import professionalIcon from '@/assets/icons/steps/professional-verification.svg';
import rationCardIcon from '@/assets/icons/steps/ration-card.svg';
import utilityBillIcon from '@/assets/icons/steps/utility-bill.svg';

/**
 * Workflow step type → designer SVG.
 *
 * The designer drew 13 icons; these are the 10 whose API maps onto a workflow
 * step type. The other four (OCR, Corporate, Business ID, Professional) are
 * catalogue-only APIs with no step type, reachable through CATALOGUE_ICONS below.
 *
 * Step types with no designer icon (bank_account, document_upload, esign,
 * kra_check, proof_of_address, credit_bureau) intentionally have no entry and
 * fall back to the lucide icon named in STEP_TYPE_META — the same icon they
 * render today, so nothing regresses while we wait for the missing artwork.
 */
const STEP_TYPE_ICON: Record<string, string> = {
  pan_verification: panIcon,
  aadhaar_otp: aadhaarIcon,
  aadhaar_verification: aadhaarIcon,
  face_match: faceMatchIcon,
  liveness: livenessIcon,
  id_verification: idVerificationIcon,
  utility_bill: utilityBillIcon,
  ration_card: rationCardIcon,
  gstin: gstIcon,
  msme: msmeIcon,
};

/**
 * Lucide fallbacks, keyed by the `icon` name in STEP_TYPE_META.
 *
 * EVERY `icon` VALUE IN STEP_TYPE_META MUST HAVE AN ENTRY HERE. A missing one does not fail loudly —
 * `FallbackIcon` silently substitutes `ShieldCheck`, so the step renders as a generic shield that
 * looks deliberate. `ClipboardList` (Questionnaire) and `Zap` (Utility Bill) were both missing, which
 * is why a workflow with three Questionnaire steps showed three identical shields in the Features
 * column while the builder sidebar drew the right icon for each.
 */
const LUCIDE_FALLBACKS: Record<string, LucideIcon> = {
  FileText,
  Smartphone,
  ScanFace,
  Eye,
  Landmark,
  CreditCard,
  Upload,
  PenLine,
  Database,
  MapPin,
  TrendingUp,
  Building2,
  ClipboardList,
  Zap,
  // Email Verification has no designer SVG — it is our own step, not one of the 13 the designer
  // drew for the API catalogue — so the lucide fallback is its real icon rather than a placeholder.
  Mail,
};

/** Human-readable name for a step type, e.g. `pan_verification` → "PAN Verification". */
export function stepLabel(stepType: string): string {
  return STEP_TYPE_META[stepType]?.label ?? stepType;
}

/**
 * Tone controls the icon's fill. `neutral` is the list-page default (the
 * designer's #445968); the status tones exist for the session screens, which
 * colour the same icons by verification outcome.
 */
export type StepIconTone = 'neutral' | 'blue' | 'green' | 'red' | 'orange' | 'purple';

/**
 * A single verification-step icon.
 *
 * Renders the designer's masked SVG when one exists for `stepType`, otherwise
 * the step's lucide icon. Hovering shows the method name in the designer's
 * dark tooltip (CSS-only — no JS, no positioning library).
 */
export function StepIcon({
  stepType,
  tone = 'neutral',
  className,
}: {
  stepType: string;
  tone?: StepIconTone;
  className?: string;
}) {
  const label = stepLabel(stepType);
  const src = STEP_TYPE_ICON[stepType];

  return (
    <span className={cn('sc-step-tooltip-wrapper', className)}>
      {src ? (
        <MaskIcon src={src} label={label} className={`sc-step-icon sc-step-icon--${tone}`} />
      ) : (
        <FallbackIcon stepType={stepType} label={label} tone={tone} />
      )}
      <span className="sc-step-tooltip" role="tooltip">
        {label}
        <span className="sc-step-tooltip__arrow" />
      </span>
    </span>
  );
}

// Lucide icons take their colour from `currentColor`, not from the mask
// background, so the tone maps to a text colour here instead.
const FALLBACK_TONE_CLASS: Record<StepIconTone, string> = {
  neutral: 'text-[#445968]',
  blue: 'text-primary',
  green: 'text-green00',
  red: 'text-redBF',
  orange: 'text-orangeDA',
  purple: 'text-[#9747FF]',
};

function FallbackIcon({
  stepType,
  label,
  tone,
}: {
  stepType: string;
  label: string;
  tone: StepIconTone;
}) {
  const name = STEP_TYPE_META[stepType]?.icon;
  const Icon = (name && LUCIDE_FALLBACKS[name]) || ShieldCheck;
  return (
    <Icon
      className={cn('h-[18px] w-[18px] shrink-0', FALLBACK_TONE_CLASS[tone])}
      role="img"
      aria-label={label}
    />
  );
}

// ── Editor catalogue ──────────────────────────────────────────────────────────

/**
 * Catalogue-entry id → designer SVG, for the editor's Steps sidebar.
 *
 * Keyed by the ids in StepCatalog's STEP_CATALOG rather than by step type,
 * because that list also carries APIs with no step type of their own. The ids
 * line up 1:1 with the designer's own icon keys — both describe the same
 * catalogue.
 */
const CATALOGUE_ICONS: Record<string, string> = {
  aadhaar: aadhaarIcon,
  pan: panIcon,
  idVerification: idVerificationIcon,
  face: faceMatchIcon,
  liveness: livenessIcon,
  ocr: ocrIcon,
  utilityBill: utilityBillIcon,
  rationCard: rationCardIcon,
  gst: gstIcon,
  corporate: corporateIcon,
  businessId: businessIdIcon,
  professional: professionalIcon,
  msme: msmeIcon,
};

/**
 * A catalogue entry's icon for the editor's Steps sidebar.
 *
 * Unlike StepIcon, this takes its colour from `currentColor`, so the row's own
 * text colour drives it — grey when the step is inactive, blue when it's in the
 * workflow. Entries the designer hasn't drawn keep the lucide icon declared on
 * the catalogue entry, so every row still has an icon.
 */
export function CatalogIcon({
  catalogueId,
  fallback: Fallback,
  className,
}: {
  catalogueId: string;
  fallback: LucideIcon;
  className?: string;
}) {
  const src = CATALOGUE_ICONS[catalogueId];
  return src ? (
    <MaskIcon src={src} className={className} />
  ) : (
    <Fallback className={cn('shrink-0', className)} />
  );
}
